import * as React from 'react';
import getPackageBaseUrl from 'owa-session-store/lib/actions/getPackageBaseUrl';
import LoadingEndState from '../store/schema/LoadingEndState';
import onErrorCardShownHandler from '../utils/actions/onErrorCardShownHandler';
import selectLoadingImage from '../utils/view/selectLoadingImage';
import showErrorAndRequestDonation from '../utils/view/showErrorAndRequestDonation';
import TailoredActionName from '../store/schema/telemetry/TailoredActionName';
import { DefaultButton } from 'office-ui-fabric-react/lib';
import { ERROR_DIALOG_LOCATION, MAX_LOADING_TIME } from '../helpers/TxpConstants';
import { observer } from 'mobx-react';
import { resolve as urlResolve } from 'url';
import { TxpDatapoint } from 'txp-data';
import TxpStatus from 'txp-data/lib/schema/TxpStatus';
import strings from 'owa-strings/owa-txp-strings';

let styles = require('./TxpLoadingAndErrorCard.scss');
const classNames = require('classnames');

export interface TxpLoadingAndErrorCardProps {
    txpStatus: TxpStatus;
    entityNamesMap: number;
    showPermanentErrorCard: boolean;
    datapointLogger?: (txpDatapoint: TxpDatapoint, datapointName: string) => void;
    onHelpUsImprove: (feedbackType?: string, feedbackLocationName?: string) => void;
    startLoadingPerfDatapoint: () => void;
    endLoadingPerfDatapoint: (loadingEndState: LoadingEndState) => void;
}

export interface TxpLoadingAndErrorCardState {
    isLoading: boolean;
}

/**
 * 1. If currently waiting for stamped TXP entities, show loading animation
 * 2. If not, show Error card with report link
 */
@observer
export default class TxpLoadingAndErrorCard extends React.Component<
    TxpLoadingAndErrorCardProps,
    TxpLoadingAndErrorCardState
> {
    private loadingTimer = null;
    private teeTriggeredError: boolean;

    constructor(props: TxpLoadingAndErrorCardProps) {
        super(props);
        const errorCardDetails = showErrorAndRequestDonation(props.txpStatus);
        this.state = {
            isLoading: !errorCardDetails.showError,
        };
        this.teeTriggeredError = errorCardDetails.teeTriggeredError;
        this.handleStateChange(null);
    }

    componentWillReceiveProps(newProps: TxpLoadingAndErrorCardProps) {
        const previousState = this.state;
        // Don't process new props if we are already showing error card to the user
        if (previousState.isLoading) {
            const errorCardDetails = showErrorAndRequestDonation(newProps.txpStatus);
            this.setState({
                isLoading: !(errorCardDetails.showError || this.props.showPermanentErrorCard),
            });
            this.teeTriggeredError = errorCardDetails.teeTriggeredError;
            this.handleStateChange(previousState);
        }
    }

    componentWillUnmount() {
        this.clearLoadingTimer();
    }

    render() {
        const entityImage: string = selectLoadingImage(
            this.state.isLoading,
            this.props.entityNamesMap
        );
        return (
            <div className={classNames(styles.loadingContainer)}>
                <img
                    className={classNames(styles.loadingImage)}
                    src={getPackageBaseUrl() && urlResolve(getPackageBaseUrl(), entityImage)}
                />
                <div className={classNames(styles.loadingTextContainer)}>
                    {this.state.isLoading ? (
                        <span className={classNames(styles.loadingText)}>
                            {strings.eventsLoadingText}
                        </span>
                    ) : (
                        <>
                            <span className={classNames(styles.loadingText)}>
                                {strings.eventsLoadingErrorText}
                            </span>
                            <DefaultButton
                                className={classNames(styles.button)}
                                text={strings.helpUsImproveText}
                                onClick={this.onHelpUsImproveClicked}
                            />
                        </>
                    )}
                </div>
            </div>
        );
    }

    private handleStateChange(previousState: TxpLoadingAndErrorCardState) {
        if (this.state.isLoading) {
            // Set timer only if it's not started already
            if (!this.loadingTimer) {
                this.props.startLoadingPerfDatapoint();
                this.loadingTimer = setTimeout(this.onTimeout, MAX_LOADING_TIME);
            }
        } else {
            this.clearLoadingTimer();
            // If an error card is to be shown, log loading end state datapoint if
            // loading UI was being shown previously. Otherwise log error card.
            if (previousState && previousState.isLoading) {
                const loadingEndState = this.props.showPermanentErrorCard
                    ? LoadingEndState.PermanentError
                    : LoadingEndState.DonationRequested;
                this.props.endLoadingPerfDatapoint(loadingEndState);
            } else {
                this.onErrorCardShown();
            }
        }
    }

    private onTimeout = () => {
        this.props.endLoadingPerfDatapoint(LoadingEndState.Timeout);
        this.setState({
            isLoading: false,
        });
        this.loadingTimer = null;
    };

    private clearLoadingTimer = () => {
        if (this.loadingTimer) {
            clearTimeout(this.loadingTimer);
            this.loadingTimer = null;
        }
    };

    private onHelpUsImproveClicked = () => {
        const feedbackType: string = this.teeTriggeredError
            ? TailoredActionName.TeeTriggeredDonation
            : TailoredActionName.ReportAnIssue;
        this.props.onHelpUsImprove(feedbackType, ERROR_DIALOG_LOCATION);
    };

    private onErrorCardShown = () => {
        const { entityNamesMap, datapointLogger, txpStatus } = this.props;
        const txpEntitiesState = txpStatus && txpStatus.state;
        onErrorCardShownHandler(entityNamesMap, txpEntitiesState, datapointLogger);
    };
}
